import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../auth/AuthContext";

type ArubaEnvironment = "DEMO" | "PRODUCTION";

type ArubaStatusResponse = {
  connected: boolean;
  provider?: string;
  environment?: ArubaEnvironment;
  username?: string;
  expiresAt?: string | null;
  lastSyncAt?: string | null;
  metadata?: {
    vatNumber?: string;
    sdiCode?: string;
    pec?: string;
    webhookNotificationsUrl?: string;
    webhookInvoicesUrl?: string;
  };
  webhookNotificationsUrl?: string;
  webhookInvoicesUrl?: string;
};

type ArubaConnectPayload = {
  environment: ArubaEnvironment;
  username: string;
  password: string;
  metadata?: {
    vatNumber?: string;
    sdiCode?: string;
    pec?: string;
    webhookNotificationsUrl?: string;
    webhookInvoicesUrl?: string;
  };
};

type SendInvoicePayload = {
  invoiceId: string;
  fileName?: string;
};

type ArubaSelfTestResponse = {
  ok?: boolean;
  message?: string;
  checks?: {
    accountActive?: boolean;
    environment?: ArubaEnvironment;
    usernamePresent?: boolean;
    tokenValid?: boolean;
    metadataPresent?: boolean;
    missingMetadata?: string[];
    webhookPublic?: boolean;
  };
  status?: ArubaStatusResponse;
};

type GenericApiResponse = {
  ok?: boolean;
  message?: string;
  data?: any;
};

type InvoiceRow = {
  id?: string;
  fileName?: string;
  filename?: string;
  documentId?: string;
  invoiceId?: string;
  status?: string;
  createdAt?: string;
  date?: string;
  supplierName?: string;
  customerName?: string;
  total?: number | string;
  [key: string]: any;
};

const API_BASE = process.env.REACT_APP_API_BASE_URL
  ? String(process.env.REACT_APP_API_BASE_URL).replace(/\/$/, "")
  : "/api";

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("it-IT", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function formatAmount(value: unknown) {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(num);
}

function normalizeArrayResponse(data: any): InvoiceRow[] {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

export default function ElectronicInvoicingArubaPage() {
  const { token } = useAuth() as any;
  const [status, setStatus] = useState<ArubaStatusResponse | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [form, setForm] = useState<ArubaConnectPayload>({
    environment: "PRODUCTION",
    username: "",
    password: "",
    metadata: {
      vatNumber: "",
      sdiCode: "",
      pec: "",
      webhookNotificationsUrl: `${window.location.origin}/api/webhooks/aruba/notifications`,
      webhookInvoicesUrl: `${window.location.origin}/api/webhooks/aruba/invoices`,
    },
  });
  const [saveLoading, setSaveLoading] = useState(false);
  const [selfTestLoading, setSelfTestLoading] = useState(false);
  const [selfTestResult, setSelfTestResult] = useState<ArubaSelfTestResponse | null>(null);
  const [sendInvoiceId, setSendInvoiceId] = useState("");
  const [sendFileName, setSendFileName] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [sentInvoices, setSentInvoices] = useState<InvoiceRow[]>([]);
  const [receivedInvoices, setReceivedInvoices] = useState<InvoiceRow[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [alert, setAlert] = useState<{
    type: "success" | "danger" | "warning" | "info";
    text: string;
  } | null>(null);

  const isConnected = !!status?.connected;

  const statusBadge = useMemo(() => {
    if (!status) return "secondary";
    return status.connected ? "success" : "secondary";
  }, [status]);

  const apiFetch = useCallback(async <T = any,>(path: string, options?: RequestInit): Promise<T> => {
    const response = await fetch(`${API_BASE}${path}`, {
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options?.headers || {}),
      },
      ...options,
    });

    const text = await response.text();
    const data = text ? JSON.parse(text) : null;
    if (!response.ok) {
      throw new Error(data?.message || `Errore API: ${response.status}`);
    }
    return data;
  }, [token]);

  const loadStatus = useCallback(async () => {
    try {
      setStatusLoading(true);
      const data = await apiFetch<ArubaStatusResponse>(
        "/electronic-invoicing/aruba/status",
      );
      setStatus(data);
      if (data?.environment) {
        setForm((prev) => ({
          ...prev,
          environment: data.environment as ArubaEnvironment,
          username: data.username || prev.username,
          password: "",
          metadata: {
            vatNumber: data?.metadata?.vatNumber || "",
            sdiCode: data?.metadata?.sdiCode || "",
            pec: data?.metadata?.pec || "",
            webhookNotificationsUrl:
              data?.metadata?.webhookNotificationsUrl || data?.webhookNotificationsUrl || "",
            webhookInvoicesUrl: data?.metadata?.webhookInvoicesUrl || data?.webhookInvoicesUrl || "",
          },
        }));
      }
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error.message || "Errore nel caricamento stato Aruba",
      });
    } finally {
      setStatusLoading(false);
    }
  }, [apiFetch]);

  const loadLists = useCallback(async () => {
    try {
      setListLoading(true);
      const query = new URLSearchParams();
      if (fromDate) query.set("from", fromDate);
      if (toDate) query.set("to", toDate);
      const suffix = query.toString() ? `?${query.toString()}` : "";
      const [sentRes, receivedRes] = await Promise.all([
        apiFetch<any>(`/electronic-invoicing/aruba/sent${suffix}`),
        apiFetch<any>(`/electronic-invoicing/aruba/received${suffix}`),
      ]);
      setSentInvoices(normalizeArrayResponse(sentRes));
      setReceivedInvoices(normalizeArrayResponse(receivedRes));
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error.message || "Errore nel caricamento elenchi Aruba",
      });
    } finally {
      setListLoading(false);
    }
  }, [apiFetch, fromDate, toDate]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  async function handleSaveConfig(e: FormEvent) {
    e.preventDefault();
    try {
      setSaveLoading(true);
      setAlert(null);
      const payload: ArubaConnectPayload = {
        environment: form.environment,
        username: form.username.trim(),
        password: form.password.trim(),
        metadata: {
          vatNumber: form.metadata?.vatNumber?.trim() || "",
          sdiCode: form.metadata?.sdiCode?.trim() || "",
          pec: form.metadata?.pec?.trim() || "",
          webhookNotificationsUrl: form.metadata?.webhookNotificationsUrl?.trim() || "",
          webhookInvoicesUrl: form.metadata?.webhookInvoicesUrl?.trim() || "",
        },
      };
      if (!payload.username || !payload.password) {
        setAlert({ type: "warning", text: "Inserisci username e password Aruba." });
        return;
      }
      const result = await apiFetch<GenericApiResponse>(
        "/electronic-invoicing/aruba/connect",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      setAlert({
        type: "success",
        text: result?.message || "Configurazione Aruba salvata correttamente.",
      });
      setForm((prev) => ({ ...prev, password: "" }));
      await loadStatus();
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error.message || "Errore salvataggio configurazione Aruba",
      });
    } finally {
      setSaveLoading(false);
    }
  }

  async function handleSendInvoice(e: FormEvent) {
    e.preventDefault();
    try {
      setSendLoading(true);
      setAlert(null);
      if (!sendInvoiceId.trim()) {
        setAlert({ type: "warning", text: "Inserisci un invoiceId valido." });
        return;
      }
      const payload: SendInvoicePayload = {
        invoiceId: sendInvoiceId.trim(),
        fileName: sendFileName.trim() || undefined,
      };
      const result = await apiFetch<GenericApiResponse>(
        "/electronic-invoicing/aruba/send",
        {
          method: "POST",
          body: JSON.stringify(payload),
        },
      );
      setAlert({
        type: "success",
        text: result?.message || "Fattura inviata ad Aruba.",
      });
      setSendInvoiceId("");
      setSendFileName("");
      await loadStatus();
      await loadLists();
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error.message || "Errore durante invio fattura",
      });
    } finally {
      setSendLoading(false);
    }
  }

  async function handleSelfTest() {
    try {
      setSelfTestLoading(true);
      setAlert(null);
      const result = await apiFetch<ArubaSelfTestResponse>("/electronic-invoicing/aruba/self-test");
      setSelfTestResult(result);
      const missing = result?.checks?.missingMetadata as string[] | undefined;
      const missingText = missing?.length ? ` Campi mancanti: ${missing.join(", ")}` : "";
      setAlert({
        type: result?.ok ? "success" : "warning",
        text: `${result?.message || "Self-test completato."}${missingText}`,
      });
      await loadStatus();
    } catch (error: any) {
      setAlert({
        type: "danger",
        text: error.message || "Errore durante self-test Aruba",
      });
    } finally {
      setSelfTestLoading(false);
    }
  }

  async function handleCopySelfTestReport() {
    if (!selfTestResult) return;
    const checks = selfTestResult.checks || {};
    const status = selfTestResult.status || {};
    const missing = checks.missingMetadata || [];

    const report = [
      "Report self-test Aruba",
      `Esito: ${selfTestResult.ok ? "OK" : "DA VERIFICARE"}`,
      `Messaggio: ${selfTestResult.message || "-"}`,
      `Provider: ${status.provider || "ARUBA"}`,
      `Ambiente: ${status.environment || "-"}`,
      `Username: ${status.username || "-"}`,
      `Token valido: ${checks.tokenValid ? "SI" : "NO"}`,
      `Dati fiscali completi: ${checks.metadataPresent ? "SI" : "NO"}`,
      `Campi mancanti: ${missing.length ? missing.join(", ") : "nessuno"}`,
      `Data report: ${new Date().toLocaleString("it-IT")}`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(report);
      setAlert({ type: "success", text: "Report self-test copiato negli appunti." });
    } catch {
      setAlert({ type: "warning", text: "Impossibile copiare automaticamente il report." });
    }
  }

  return (
    <div className="container py-4">
      <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-4">
        <div>
          <h1 className="h3 mb-1">Fatturazione Elettronica Aruba</h1>
          <p className="text-muted mb-0">
            Configurazione integrazione Aruba, stato connessione, invio e sincronizzazione documenti.
          </p>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-primary"
            onClick={loadStatus}
            disabled={statusLoading}
            type="button"
          >
            {statusLoading ? "Aggiornamento..." : "Aggiorna stato"}
          </button>

          <button
            className="btn btn-outline-secondary"
            onClick={loadLists}
            disabled={listLoading || !isConnected}
            type="button"
          >
            {listLoading ? "Caricamento..." : "Sincronizza elenchi"}
          </button>
        </div>
      </div>

      {alert && (
        <div className={`alert alert-${alert.type}`} role="alert">
          {alert.text}
        </div>
      )}

      <div className="row g-4">
        <div className="col-12 col-xl-4">
          <div className="card shadow-sm border-0 h-100">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="h5 mb-0">Stato connessione</h2>
                <span className={`badge text-bg-${statusBadge}`}>
                  {status?.connected ? "Connesso" : "Non connesso"}
                </span>
              </div>

              <div className="small">
                <div className="mb-2">
                  <strong>Provider:</strong> {status?.provider || "ARUBA"}
                </div>
                <div className="mb-2">
                  <strong>Ambiente:</strong> {status?.environment || "—"}
                </div>
                <div className="mb-2">
                  <strong>Username:</strong> {status?.username || "—"}
                </div>
                <div className="mb-2">
                  <strong>Partita IVA:</strong> {status?.metadata?.vatNumber || "—"}
                </div>
                <div className="mb-2">
                  <strong>Codice SDI:</strong> {status?.metadata?.sdiCode || "—"}
                </div>
                <div className="mb-2">
                  <strong>PEC:</strong> {status?.metadata?.pec || "—"}
                </div>
                <div className="mb-2">
                  <strong>Webhook notifiche:</strong> {status?.metadata?.webhookNotificationsUrl || status?.webhookNotificationsUrl || "—"}
                </div>
                <div className="mb-2">
                  <strong>Webhook fatture ricevute:</strong> {status?.metadata?.webhookInvoicesUrl || status?.webhookInvoicesUrl || "—"}
                </div>
                <div className="mb-2">
                  <strong>Scadenza token:</strong> {formatDateTime(status?.expiresAt)}
                </div>
                <div className="mb-0">
                  <strong>Ultimo sync:</strong> {formatDateTime(status?.lastSyncAt)}
                </div>
              </div>

              <hr />

              <div className="small text-muted">
                Usa questa sezione per collegare NAT al tuo account Aruba e verificare se il token è attivo.
              </div>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-8">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h2 className="h5 mb-3">Configurazione Aruba</h2>

              <form onSubmit={handleSaveConfig}>
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label className="form-label">Ambiente</label>
                    <select
                      className="form-select"
                      value={form.environment}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          environment: e.target.value as ArubaEnvironment,
                        }))
                      }
                    >
                      <option value="DEMO">Demo</option>
                      <option value="PRODUCTION">Produzione</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label">Username Aruba</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.username}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          username: e.target.value,
                        }))
                      }
                      placeholder="Inserisci username Aruba"
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label">Password Aruba</label>
                    <input
                      type="password"
                      className="form-control"
                      value={form.password}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          password: e.target.value,
                        }))
                      }
                      placeholder="Inserisci password Aruba"
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label">Partita IVA</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.metadata?.vatNumber || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          metadata: { ...(prev.metadata || {}), vatNumber: e.target.value },
                        }))
                      }
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label">Codice SDI</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.metadata?.sdiCode || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          metadata: { ...(prev.metadata || {}), sdiCode: e.target.value.toUpperCase() },
                        }))
                      }
                    />
                  </div>

                  <div className="col-12 col-md-4">
                    <label className="form-label">PEC</label>
                    <input
                      type="email"
                      className="form-control"
                      value={form.metadata?.pec || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          metadata: { ...(prev.metadata || {}), pec: e.target.value },
                        }))
                      }
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Webhook notifiche Aruba (pubblico)</label>
                    <input
                      type="url"
                      className="form-control"
                      value={form.metadata?.webhookNotificationsUrl || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          metadata: { ...(prev.metadata || {}), webhookNotificationsUrl: e.target.value },
                        }))
                      }
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Webhook fatture ricevute (pubblico)</label>
                    <input
                      type="url"
                      className="form-control"
                      value={form.metadata?.webhookInvoicesUrl || ""}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          metadata: { ...(prev.metadata || {}), webhookInvoicesUrl: e.target.value },
                        }))
                      }
                    />
                  </div>
                </div>

                <div className="mt-3 d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={saveLoading}
                  >
                    {saveLoading ? "Salvataggio..." : "Salva configurazione"}
                  </button>

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={loadStatus}
                    disabled={statusLoading}
                  >
                    Verifica stato
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-success"
                    onClick={handleSelfTest}
                    disabled={selfTestLoading}
                  >
                    {selfTestLoading ? "Test in corso..." : "Esegui self-test Aruba"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <h2 className="h5 mb-0">Esito self-test</h2>
                <div className="d-flex gap-2">
                  {selfTestResult && (
                    <button type="button" className="btn btn-sm btn-outline-secondary" onClick={handleCopySelfTestReport}>
                      Copia report self-test
                    </button>
                  )}
                  <span className={`badge text-bg-${selfTestResult?.ok ? "success" : "secondary"}`}>
                    {selfTestResult ? (selfTestResult.ok ? "OK" : "Da verificare") : "Non eseguito"}
                  </span>
                </div>
              </div>

              {!selfTestResult ? (
                <div className="text-muted small">
                  Clicca su <strong>Esegui self-test Aruba</strong> per visualizzare la checklist tecnica.
                </div>
              ) : (
                <>
                  <div
                    className={`alert ${selfTestResult.ok ? "alert-success" : "alert-warning"} py-2 mb-3`}
                    role="alert"
                  >
                    {selfTestResult.message || "Self-test completato"}
                  </div>
                  <ul className="list-group">
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      <span>Account Aruba attivo</span>
                      <span className={`badge text-bg-${selfTestResult?.checks?.accountActive ? "success" : "danger"}`}>
                        {selfTestResult?.checks?.accountActive ? "OK" : "KO"}
                      </span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      <span>Username configurato</span>
                      <span className={`badge text-bg-${selfTestResult?.checks?.usernamePresent ? "success" : "danger"}`}>
                        {selfTestResult?.checks?.usernamePresent ? "OK" : "KO"}
                      </span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      <span>Token Aruba valido</span>
                      <span className={`badge text-bg-${selfTestResult?.checks?.tokenValid ? "success" : "danger"}`}>
                        {selfTestResult?.checks?.tokenValid ? "OK" : "KO"}
                      </span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      <span>Dati fiscali completi (P.IVA, SDI, PEC)</span>
                      <span className={`badge text-bg-${selfTestResult?.checks?.metadataPresent ? "success" : "warning"}`}>
                        {selfTestResult?.checks?.metadataPresent ? "OK" : "INCOMPLETI"}
                      </span>
                    </li>
                    <li className="list-group-item d-flex justify-content-between align-items-center">
                      <span>Webhook pubblici HTTPS configurati</span>
                      <span className={`badge text-bg-${selfTestResult?.checks?.webhookPublic ? "success" : "warning"}`}>
                        {selfTestResult?.checks?.webhookPublic ? "OK" : "NO"}
                      </span>
                    </li>
                    {!!selfTestResult?.checks?.missingMetadata?.length && (
                      <li className="list-group-item">
                        <span className="small text-muted">
                          Campi mancanti: {selfTestResult.checks.missingMetadata.join(", ")}
                        </span>
                      </li>
                    )}
                  </ul>
                </>
              )}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h2 className="h5 mb-3">Invio fattura di test</h2>

              <form onSubmit={handleSendInvoice}>
                <div className="row g-3">
                  <div className="col-12 col-md-4">
                    <label className="form-label">Invoice ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={sendInvoiceId}
                      onChange={(e) => setSendInvoiceId(e.target.value)}
                      placeholder="es. clx123456"
                    />
                  </div>

                  <div className="col-12 col-md-5">
                    <label className="form-label">Nome file XML (opzionale)</label>
                    <input
                      type="text"
                      className="form-control"
                      value={sendFileName}
                      onChange={(e) => setSendFileName(e.target.value)}
                      placeholder="es. IT01234567890_0001.xml"
                    />
                  </div>

                  <div className="col-12 col-md-3 d-flex align-items-end">
                    <button
                      type="submit"
                      className="btn btn-success w-100"
                      disabled={sendLoading || !isConnected}
                    >
                      {sendLoading ? "Invio..." : "Invia fattura"}
                    </button>
                  </div>
                </div>
              </form>

              {!isConnected && (
                <div className="alert alert-warning mt-3 mb-0">
                  Prima salva una configurazione Aruba valida.
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-3 mb-3">
                <h2 className="h5 mb-0">Filtri sincronizzazione</h2>

                <div className="d-flex flex-column flex-md-row gap-2">
                  <input
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                  <input
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={loadLists}
                    disabled={listLoading || !isConnected}
                  >
                    {listLoading ? "Caricamento..." : "Carica documenti"}
                  </button>
                </div>
              </div>
              <p className="small text-muted mb-3">
                Origine dati: Aruba API live (`/api/electronic-invoicing/aruba/sent` e
                ` /api/electronic-invoicing/aruba/received`).
              </p>

              <div className="row g-4">
                <div className="col-12 col-xl-6">
                  <h3 className="h6 mb-3">Fatture inviate</h3>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle">
                      <thead>
                        <tr>
                          <th>ID/Documento</th>
                          <th>File</th>
                          <th>Stato</th>
                          <th>Data</th>
                          <th>Totale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sentInvoices.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-muted text-center py-4">
                              Nessuna fattura inviata caricata.
                            </td>
                          </tr>
                        ) : (
                          sentInvoices.map((item, index) => (
                            <tr key={item.id || item.documentId || item.invoiceId || index}>
                              <td>{item.documentId || item.invoiceId || item.id || "—"}</td>
                              <td>{item.fileName || item.filename || "—"}</td>
                              <td>
                                <span className="badge text-bg-light border">
                                  {item.status || "—"}
                                </span>
                              </td>
                              <td>{formatDateTime(item.createdAt || item.date)}</td>
                              <td>{formatAmount(item.total)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="col-12 col-xl-6">
                  <h3 className="h6 mb-3">Fatture ricevute</h3>
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle">
                      <thead>
                        <tr>
                          <th>ID/Documento</th>
                          <th>Fornitore</th>
                          <th>Stato</th>
                          <th>Data</th>
                          <th>Totale</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receivedInvoices.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-muted text-center py-4">
                              Nessuna fattura ricevuta caricata.
                            </td>
                          </tr>
                        ) : (
                          receivedInvoices.map((item, index) => (
                            <tr key={item.id || item.documentId || item.invoiceId || index}>
                              <td>{item.documentId || item.invoiceId || item.id || "—"}</td>
                              <td>{item.supplierName || item.customerName || "—"}</td>
                              <td>
                                <span className="badge text-bg-light border">
                                  {item.status || "—"}
                                </span>
                              </td>
                              <td>{formatDateTime(item.createdAt || item.date)}</td>
                              <td>{formatAmount(item.total)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
